import type { TrackedTranslation } from '@ps/web-core-intl';
import React, { useState } from 'react';
import { useField, useForm } from 'react-final-form';
import { useSelector, useStore } from 'react-redux';
import { lastValueFrom } from 'rxjs';

import useRequest from '@swf/core/hooks/useRequest';
import type { RootState, Store } from '@swf/core/store';
import { select } from '@swf/core/store';

import { Error } from '../../../../../../types/paymentVerification';
import CTAButton from '../../../../../CTAButton/CTAButton';
import { fieldName } from '../ConfirmationCheckbox/ConfirmationCheckbox';

interface Props {
  numberOfAddedFiles: number;
  translation: TrackedTranslation;
  data: File[];
  setError: (error: Error) => void;
}

const SubmitButton: React.FC<Props> = ({ data, numberOfAddedFiles, translation, setError }) => {
  const [loading, setLoading] = useState(false);
  const form = useForm();
  const request = useRequest();
  const wc2Domain = useSelector(select.location.getWC2Domain);
  const store = useStore<RootState>() as Store;
  const { token, signature, userWebId } = select.session.getLegacySession(store.getState());
  const {
    input: { value: checkboxChecked }
  } = useField(fieldName, {
    subscription: { value: true }
  });

  const packFiles = (fillesForUpload: File[]) => {
    const files = new FormData();
    fillesForUpload.forEach((file) => {
      files.append('files', file);
    });
    return files;
  };

  const files = packFiles(data);

  const submitDocumentsWC2 = async () => {
    if (!checkboxChecked) {
      setLoading(false);
      form.submit();
      return;
    }
    try {
      await lastValueFrom(
        request({
          http: true,
          method: 'POST',
          httpDomain: wc2Domain,
          service: '/wc2/rest/document/upload',
          headers: {
            authorization: `PsToken2 ${window.btoa(
              [window.btoa(userWebId || ''), token, signature].join(':')
            )}`
          },
          body: files
        })
      );
      setLoading(false);
      form.submit();
    } catch (error) {
      setLoading(false);
      setError(Error.Server);
    }
  };

  const onClick = async (e: React.MouseEvent<Element, MouseEvent>) => {
    e.preventDefault();
    setLoading(true);
    if (numberOfAddedFiles === 0) {
      setLoading(false);
      setError(Error.UploadFile);
      if (!checkboxChecked) {
        form.submit();
      }
      return;
    }
    submitDocumentsWC2();
  };

  return <CTAButton translation={translation} type="submit" onClick={onClick} loading={loading} />;
};

SubmitButton.displayName = 'SubmitButton';

export default SubmitButton;
